import React, { useState, useEffect } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { CustomQuestion } from "./CallScriptManager";
import { AlertCircle } from "lucide-react";

// Category options
const CATEGORIES = [
  { value: "PlanInformation", label: "Plan Information" },
  { value: "Maximums", label: "Maximums" },
  { value: "ClaimsInformation", label: "Claims Information" },
  { value: "BenefitsDetails", label: "Benefits Details" },
  { value: "ExtraDentalInfo", label: "Extra Dental Info" },
  { value: "ExtraMedicalInfo", label: "Extra Medical Info" },
  { value: "CallDetails", label: "Call Details" },
];

const EXPECTED_RESPONSE_TYPES = [
  { value: "string", label: "Text (String)" },
  { value: "float", label: "Number (Float)" },
  { value: "boolean", label: "Yes/No (Boolean)" },
  { value: "date", label: "Date" },
];

interface QuestionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question?: CustomQuestion | null;
  onSave: (question: Omit<CustomQuestion, "id">) => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  open,
  onOpenChange,
  question,
  onSave,
}) => {
  const [questionText, setQuestionText] = useState("");
  const [category, setCategory] = useState<string>("PlanInformation");
  const [expectedResponseType, setExpectedResponseType] = useState<string>("string");
  const [procedureCode, setProcedureCode] = useState<string>("");
  const [originalResultsFieldPath, setOriginalResultsFieldPath] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const requiresProcedureCode = category === "BenefitsDetails";
  const isKnownProcedureCodeQuestion = question?.results_field_path?.startsWith("procedure_codes.") ?? false;

  // Extract the original procedure code from results_field_path if it exists
  // Pattern: procedure_codes.{PROCEDURE_CODE}.field_name
  const extractProcedureCodeFromPath = (path: string | null): string | null => {
    if (!path || !path.startsWith("procedure_codes.")) return null;
    const parts = path.split(".");
    if (parts.length >= 2) {
      return parts[1];
    }
    return null;
  };

  // Reset form when dialog opens/closes or question changes
  useEffect(() => {
    if (open) {
      if (question) {
        // Edit mode
        setQuestionText(question.question_text || "");
        setCategory(question.category || "PlanInformation");
        setExpectedResponseType(question.expected_response_type || "string");
        setProcedureCode(question.procedure_code || "");
        setOriginalResultsFieldPath(question.results_field_path || null);
      } else {
        // Create mode
        setQuestionText("");
        setCategory("PlanInformation");
        setExpectedResponseType("string");
        setProcedureCode("");
        setOriginalResultsFieldPath(null);
      }
      setErrors({});
    }
  }, [open, question]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate question text
    if (!questionText.trim()) {
      newErrors.questionText = "Question text is required.";
    } else if (questionText.trim().length < 10) {
      newErrors.questionText = "Question text must be at least 10 characters";
    }

    // Validate procedure code for BenefitsDetails
    if (requiresProcedureCode && !procedureCode.trim()) {
      newErrors.procedureCode = "Procedure code is required for Benefits Details questions";
    }

    if (procedureCode.trim() && procedureCode.trim().toUpperCase() === "XYZ") {
      newErrors.procedureCode = "Placeholder procedure code 'XYZ' cannot be used. Please provide an actual procedure code.";
    }

    if (isKnownProcedureCodeQuestion && !procedureCode.trim()) {
      newErrors.procedureCode = "Procedure code is required and cannot be empty.";
    }

    if (!category) {
      newErrors.category = "Category is required";
    }

    if (!expectedResponseType) {
      newErrors.expectedResponseType = "Expected response type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    let resultsFieldPath: string | null = null;
    if (isKnownProcedureCodeQuestion && originalResultsFieldPath) {
      const oldProcedureCode = extractProcedureCodeFromPath(originalResultsFieldPath);
      const newProcedureCode = procedureCode.trim();

      if (oldProcedureCode && newProcedureCode) {
        resultsFieldPath = originalResultsFieldPath.replace(
          new RegExp(`procedure_codes\\.${oldProcedureCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.`, 'g'),
          `procedure_codes.${newProcedureCode}.`
        );
      } else {
        resultsFieldPath = originalResultsFieldPath.replace(
          /procedure_codes\.([^.]+)\./,
          `procedure_codes.${newProcedureCode}.`
        );
      }
    }

    const questionData: Omit<CustomQuestion, "id"> = {
      question_text: questionText.trim(),
      category: category,
      expected_response_type: expectedResponseType,
      procedure_code: procedureCode.trim() || null,
      results_field_path: resultsFieldPath,
      is_custom: true,
    };

    onSave(questionData);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {question
              ? (isKnownProcedureCodeQuestion
                  ? "Edit Procedure Code Question"
                  : "Edit Custom Question")
              : "Create Custom Question"}
          </DialogTitle>
          <DialogDescription>
            {question
              ? (isKnownProcedureCodeQuestion
                  ? "Update the procedure code. Category and response type cannot be changed for sample questions."
                  : "Update the details of your custom question.")
              : "Create a new custom question for your call script."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Question Text */}
          <div className="space-y-2">
            <label htmlFor="question-text" className="text-sm font-medium text-gray-700">
              Question Text <span className="text-red-500">*</span>
              {isKnownProcedureCodeQuestion && (
                <span className="text-xs text-gray-500 ml-2 font-normal">(cannot be changed for sample questions)</span>
              )}
            </label>
            <Textarea
              id="question-text"
              placeholder="Enter your question here..."
              value={questionText}
              disabled={isKnownProcedureCodeQuestion}
              onChange={(e) => {
                setQuestionText(e.target.value);
                if (errors.questionText) {
                  setErrors({ ...errors, questionText: "" });
                }
              }}
              className={`min-h-[100px] ${
                errors.questionText ? "border-red-500 focus:ring-red-500" : ""
              }`}
            />
            {errors.questionText && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.questionText}
              </p>
            )}
            {questionText.length > 0 && questionText.length < 10 && (
              <p className="text-xs text-gray-500">
                {10 - questionText.length} more characters required
              </p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label htmlFor="category" className="text-sm font-medium text-gray-700">
              Category <span className="text-red-500">*</span>
              {isKnownProcedureCodeQuestion && (
                <span className="text-xs text-gray-500 ml-2 font-normal">(cannot be changed for sample questions)</span>
              )}
            </label>
            <Select value={category} onValueChange={setCategory} disabled={isKnownProcedureCodeQuestion}>
              <SelectTrigger
                id="category"
                className={errors.category ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.category}
              </p>
            )}
          </div>

          {/* Expected Response Type */}
          <div className="space-y-2">
            <label htmlFor="expected-response-type" className="text-sm font-medium text-gray-700">
              Expected Response Type <span className="text-red-500">*</span>
              {isKnownProcedureCodeQuestion && (
                <span className="text-xs text-gray-500 ml-2 font-normal">(cannot be changed for sample questions)</span>
              )}
            </label>
            <Select value={expectedResponseType} onValueChange={setExpectedResponseType} disabled={isKnownProcedureCodeQuestion}>
              <SelectTrigger
                id="expected-response-type"
                className={errors.expectedResponseType ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select response type" />
              </SelectTrigger>
              <SelectContent>
                {EXPECTED_RESPONSE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.expectedResponseType && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.expectedResponseType}
              </p>
            )}
          </div>

          {/* Procedure Code (conditional) */}
          {requiresProcedureCode && (
            <div className="space-y-2">
              <label htmlFor="procedure-code" className="text-sm font-medium text-gray-700">
                Procedure Code <span className="text-red-500">*</span>
              </label>
              <Input
                id="procedure-code"
                placeholder="e.g., D0120, 90791"
                value={procedureCode}
                onChange={(e) => {
                  setProcedureCode(e.target.value);
                  if (errors.procedureCode) {
                    setErrors({ ...errors, procedureCode: "" });
                  }
                }}
                className={errors.procedureCode ? "border-red-500 focus:ring-red-500" : ""}
              />
              {errors.procedureCode && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.procedureCode}
                </p>
              )}
            </div>
          )}

        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="bg-[#005151] hover:bg-[#004040] text-white"
            disabled={!questionText.trim() || questionText.trim().length < 10}
          >
            {question ? "Update Question" : "Create Question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionForm;
